import { HttpErrorResponse } from '@angular/common/http';
import { effect, Inject, Injectable, OnDestroy, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AutomataService,
  Automation,
  AutomationConfigurationV2,
  AutomationInterface,
  AutomationSettingsInterface,
  AutomationType,
  Context,
  FieldMaskInterface,
  IsAutomationAcceptingNewEvents,
  IsAutomationAPublishedDefaultAutomation,
  IsAutomationAPublishedTemplate,
  IsAutomationStopped,
  RunningState,
} from '@vendasta/automata';
import { combineLatest, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { TurnOffDialogResult } from '../dialogs/turn-off-automation/turn-off-automation-dialog.component';
import {
  AutomataContextService,
  AUTOMATION_NAMESPACE_INJECTION_TOKEN$,
  ContextConfigMap,
  CurrentAutomationService,
} from '@galaxy/automata/shared';
import { DialogResult as TurnOnDialogResult } from '../dialogs/turn-on-automation/dialog-result';
import { AutomationService } from './automation.service';
import { PUBLISHING_PID } from '../common/constants';
import { AutomationsRouterService } from '../shared/automations-router.service';
import { LocalStorageService } from '@vendasta/uikit';

const PLATFORM_MANAGEMENT_NAMESPACE = 'VCOR'; // THe namespace we are in when editing the system automations/templates

// ViewMode is determined by the users namespace versus the namespace of the automation
// Today, only an automation in the same namespace as the user can be edited
export enum ViewMode {
  Edit, // The user can edit the automation
  ReadOnly, // The user can only look at the automation
  PlatformManagement, // A special VCOR view when editing templates and system automations
}

function areSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) {
    return false;
  }

  for (const item of a) {
    if (!b.has(item)) {
      return false;
    }
  }

  return true;
}

/* AutomationViewService
 * Uses the current route to switch the action between which types it should correspond with.
 */
@Injectable({ providedIn: 'root' })
export class AutomationViewService implements OnDestroy {
  public automationContext = this.automationContextService.currentContext;
  contextConfig = ContextConfigMap[this.automationContext];
  private destroy$$ = new Subject<void>();

  private viewMode$: Observable<ViewMode> = this.getViewMode();

  public expandedGroups = signal<Set<string>>(new Set(), { equal: areSetsEqual });
  public newlyExpandedGroups = signal<Set<string>>(new Set(), { equal: areSetsEqual });
  private previouslyExpandedGroups: Set<string> | undefined;

  public automationType$: Observable<AutomationType> = this.getAutomationType();

  private showMissingPermissions$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public showMissingPermissions$: Observable<boolean> = this.showMissingPermissions$$.asObservable();

  private isTemplateView$: Observable<boolean> = this.automationType$.pipe(
    map((automationType) => automationType === AutomationType.AUTOMATION_TYPE_TEMPLATE),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private isBuiltInAutomationView$: Observable<boolean> = this.automationType$.pipe(
    map((automationType) => automationType === AutomationType.AUTOMATION_TYPE_DEFAULT),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public isManageBuiltInAutomationsView$: Observable<boolean> = combineLatest([
    this.isBuiltInAutomationView$,
    this.viewMode$,
  ]).pipe(
    map(([isBuiltIn, viewMode]) => {
      return isBuiltIn && viewMode === ViewMode.PlatformManagement;
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public tabsToShow$ = combineLatest([this.isTemplateView$, this.isManageBuiltInAutomationsView$]).pipe(
    map(([isTemplateView, isManageBuiltInAutomationsView]) => {
      const tabs = [
        { path: 'workflow', label: 'AUTOMATIONS.COMMON.WORKFLOW' },
        { path: 'settings', label: 'AUTOMATIONS.COMMON.SETTINGS' },
      ];
      if (!isTemplateView && !isManageBuiltInAutomationsView) {
        tabs.push({ path: 'activity', label: 'AUTOMATIONS.COMMON.ACTIVITY' });
      }
      return tabs;
    }),
  );

  public previousPageInfo$: Observable<{ titleKey: string; url: string }> = combineLatest([
    this.isTemplateView$,
    this.isBuiltInAutomationView$,
    this.isManageBuiltInAutomationsView$,
    this.automationRouter.routePrefix$,
    this.viewMode$,
  ]).pipe(
    map(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView, prefix, viewMode]) => {
      if (isTemplateView) {
        if (viewMode === ViewMode.PlatformManagement) {
          return {
            titleKey: 'AUTOMATIONS.COMMON.TEMPLATES',
            url: `${prefix}/automations/manage-templates`,
          };
        }
        if (viewMode === ViewMode.Edit) {
          return {
            titleKey: 'AUTOMATIONS.COMMON.TEMPLATES',
            url: `${prefix}/automations/config/smb/templates`,
          };
        }
        return {
          titleKey: 'AUTOMATIONS.COMMON.TEMPLATES',
          url: `${prefix}/automations/create-from-template',`,
        };
      }
      if (isManageBuiltInAutomationsView) {
        return {
          titleKey: 'AUTOMATIONS.COMMON.MANAGE_BUILT_IN_AUTOMATIONS',
          url: `${prefix}/automations/manage-built-in-automations`,
        };
      }
      if (isBuiltInAutomationView) {
        if (this.automationContext == Context.AUTOMATION_CONTEXT_SMB) {
          return {
            titleKey: 'AUTOMATIONS.COMMON.AUTOMATIONS',
            url: `${prefix}${this.contextConfig?.viewAutomationsRoute}`,
          };
        }
        if (viewMode === ViewMode.PlatformManagement) {
          return {
            titleKey: 'AUTOMATIONS.COMMON.BUILT_IN_AUTOMATIONS',
            url: `${prefix}/automations/manage-built-in-automations`,
          };
        }
        return {
          titleKey: 'AUTOMATIONS.COMMON.BUILT_IN_AUTOMATIONS',
          url: `${prefix}/automations/system-automations`,
        };
      }
      return {
        titleKey: 'AUTOMATIONS.COMMON.AUTOMATIONS',
        url: `${prefix}${this.contextConfig?.viewAutomationsRoute}`,
      };
    }),
  );

  constructor(
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
    private readonly automationContextService: AutomataContextService,
    private readonly automationRouter: AutomationsRouterService,
    private readonly translateService: TranslateService,
    private readonly automationService: AutomationService,
    private readonly localStorageService: LocalStorageService,
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly automataService: AutomataService,
  ) {
    effect(() => {
      if (!this.currentAutomationService.automation()) {
        return;
      }
      // Keeping the local storage in sync with the expandedGroups signal
      const expandedGroups = new Set<string>();
      this.currentAutomationService.automation()?.groups?.forEach((group) => {
        if (this.localStorageService.get(group.id)) {
          expandedGroups.add(group.id);
        }
      });
      this.expandedGroups.set(expandedGroups);
      // Calculate newly expanded panels
      if (this.previouslyExpandedGroups === undefined) {
        this.newlyExpandedGroups.set(new Set<string>());
      } else {
        const previouslyExpandedGroups = Array.from(this.previouslyExpandedGroups);
        const currentlyExpandedGroups = Array.from(expandedGroups);
        this.newlyExpandedGroups.set(
          new Set<string>(currentlyExpandedGroups.filter((group) => !previouslyExpandedGroups.includes(group))),
        );
      }
      this.previouslyExpandedGroups = this.expandedGroups();
    });
    effect(() => {
      // Whenever the current automation is refreshed recalculate if the user has permission
      const automationId = this.currentAutomationService.automationRefreshed();
      if (!automationId) {
        return false;
      } else {
        this.hasAutomationPermissions(automationId)
          .pipe(take(1))
          .subscribe((value) => this.showMissingPermissions$$.next(!value));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  isPartnerViewingBuiltInAutomation(): Observable<boolean> {
    return combineLatest([this.isBuiltInAutomationView$, this.isManageBuiltInAutomationsView$]).pipe(
      map(
        ([isBuiltInAutomationView, isManageBuiltInAutomationsView]) =>
          !!(isBuiltInAutomationView && !isManageBuiltInAutomationsView),
      ),
    );
  }

  isAdminView(): Observable<boolean> {
    return combineLatest([this.isTemplateView$, this.isManageBuiltInAutomationsView$]).pipe(
      map(([isTemplateView, isManageBuiltInAutomationsView]) => !!(isTemplateView || isManageBuiltInAutomationsView)),
    );
  }

  isTemplateView(): Observable<boolean> {
    return this.isTemplateView$;
  }

  isBuiltInAutomationView(): Observable<boolean> {
    return this.isBuiltInAutomationView$;
  }

  isManageBuiltInAutomationsView(): Observable<boolean> {
    return this.isManageBuiltInAutomationsView$;
  }

  isNonPartnerAutomation(): Observable<boolean> {
    return combineLatest([this.isTemplateView$, this.isBuiltInAutomationView$]).pipe(
      map(([isTemplateView, isBuiltInAutomationView]) => isTemplateView || isBuiltInAutomationView),
    );
  }

  get(id: string): Observable<Automation> {
    return this.automataService.getAutomation(id);
  }

  isOn$(automation: AutomationInterface, config?: AutomationConfigurationV2): Observable<boolean> {
    return this.isPartnerViewingBuiltInAutomation().pipe(
      map((isPartnerViewingBuiltInAutomation) => {
        if (automation.isTemplate) {
          return IsAutomationAPublishedTemplate(automation);
        }
        if (automation.isDefaultAutomation) {
          if (isPartnerViewingBuiltInAutomation) {
            return !config?.disabled;
          }
          return IsAutomationAPublishedDefaultAutomation(automation);
        }
        return IsAutomationAcceptingNewEvents(automation);
      }),
    );
  }

  isEditable$(automation: AutomationInterface): Observable<boolean> {
    return this.isPartnerViewingBuiltInAutomation().pipe(
      map((isPartnerViewingBuiltInAutomation) => {
        if (automation?.isTemplate) {
          return !IsAutomationAPublishedTemplate(automation);
        }
        if (automation?.isDefaultAutomation) {
          if (isPartnerViewingBuiltInAutomation) {
            return false;
          }
          return !IsAutomationAPublishedDefaultAutomation(automation);
        }
        return IsAutomationStopped(automation);
      }),
    );
  }

  openTurnOnDialog(a: Automation): Observable<TurnOnDialogResult | null> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      take(1),
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.openTurnOnDialog(a, AutomationType.AUTOMATION_TYPE_TEMPLATE);
        } else if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.openTurnOnDialog(a, AutomationType.AUTOMATION_TYPE_DEFAULT);
          } else {
            const r = { automationId: a.id, subscribe: false };
            return of(r);
          }
        }
        return this.automationService.openTurnOnDialog(a, AutomationType.AUTOMATION_TYPE_STANDARD);
      }),
    );
  }

  turnOn(id: string, settings?: AutomationSettingsInterface, subscribeToNotifications?: boolean): Observable<string> {
    return combineLatest([
      this.namespace$,
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([partnerId, isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.turnOn(
            id,
            AutomationType.AUTOMATION_TYPE_TEMPLATE,
            settings,
            undefined,
            subscribeToNotifications,
          );
        }
        if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.turnOn(
              id,
              AutomationType.AUTOMATION_TYPE_DEFAULT,
              settings,
              undefined,
              subscribeToNotifications,
            );
          }
          return this.automationService.turnOn(
            id,
            AutomationType.AUTOMATION_TYPE_DEFAULT,
            undefined,
            {
              partnerId: partnerId,
            },
            subscribeToNotifications,
          );
        }
        return this.automationService.turnOn(
          id,
          AutomationType.AUTOMATION_TYPE_STANDARD,
          settings,
          undefined,
          subscribeToNotifications,
        );
      }),
    );
  }

  afterOnAction(a: Automation): void {
    this.automationService.afterOnAction(a);
  }

  openTurnOffDialog(a: Automation): Observable<TurnOffDialogResult | null> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.openTurnOffDialog(a, AutomationType.AUTOMATION_TYPE_TEMPLATE);
        } else if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.openTurnOffDialog(a, AutomationType.AUTOMATION_TYPE_DEFAULT);
          } else {
            return this.automationService.openTurnOffDialog(a, AutomationType.AUTOMATION_TYPE_DEFAULT, true);
          }
        }
        return this.automationService.openTurnOffDialog(a, AutomationType.AUTOMATION_TYPE_STANDARD);
      }),
    );
  }

  turnOff(id: string, runningState?: RunningState): Observable<boolean> {
    return combineLatest([
      this.namespace$,
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([partnerId, isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.turnOff(id, AutomationType.AUTOMATION_TYPE_TEMPLATE);
        }
        if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.turnOff(id, AutomationType.AUTOMATION_TYPE_DEFAULT);
          }
          return this.automationService.turnOff(id, AutomationType.AUTOMATION_TYPE_DEFAULT, {
            partnerId: partnerId,
          });
        }
        return this.automationService.turnOff(id, AutomationType.AUTOMATION_TYPE_STANDARD, undefined, runningState);
      }),
    );
  }

  getErrorTurningOnMessage(a: Automation, hasErrors: boolean, err: HttpErrorResponse): string {
    return this.automationService.getErrorTurningOnMessage(a, hasErrors, err);
  }

  getErrorTurningOffMessage(a: Automation): string {
    return this.automationService.getErrorTurningOffMessage(a);
  }

  update(a: Automation, fieldMask: FieldMaskInterface): Observable<boolean> {
    return combineLatest([this.isBuiltInAutomationView$, this.isManageBuiltInAutomationsView$]).pipe(
      switchMap(([isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isBuiltInAutomationView && !isManageBuiltInAutomationsView) {
          // Cannot update a built-in (default) automation outside of the manage view
          return throwError('Cannot perform update');
        }
        return this.automationService.update(a, fieldMask);
      }),
    );
  }

  getInsufficientPermissionsCantTurnOnAutomationMessage(): Observable<string> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView || isBuiltInAutomationView || isManageBuiltInAutomationsView) {
          return of('');
        }
        return this.translateService.stream('AUTOMATIONS.PERMISSIONS.WARNING_MESSAGE');
      }),
    );
  }

  getMissingWorkflowCantTurnOnAutomationMessage(): Observable<string> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView || isBuiltInAutomationView || isManageBuiltInAutomationsView) {
          return of('');
        }
        return this.translateService.stream('AUTOMATIONS.EDITOR.AUTOMATION.MISSING_WORKFLOW');
      }),
    );
  }

  getInsufficientPermissionsCantTurnOnAutomationTitle(): Observable<string> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView || isBuiltInAutomationView || isManageBuiltInAutomationsView) {
          return of('');
        }
        return this.translateService.stream('AUTOMATIONS.PERMISSIONS.WARNING_TITLE');
      }),
    );
  }

  hasAutomationPermissions(automationId: string): Observable<boolean> {
    return this.automationService.hasAutomationPermissions(automationId);
  }

  getStartEditingMessage(): Observable<string> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.getStartEditingMessage(AutomationType.AUTOMATION_TYPE_TEMPLATE);
        }
        if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.getStartEditingMessage(AutomationType.AUTOMATION_TYPE_DEFAULT);
          }
          // Partner built-in (default) automations cannot be edited, they are just copied or turned off
          throw of('');
        }
        return this.automationService.getStartEditingMessage(AutomationType.AUTOMATION_TYPE_STANDARD);
      }),
    );
  }

  getEditingPreventedWarning(automation: AutomationInterface): Observable<string> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.getEditingPreventedWarning(automation, AutomationType.AUTOMATION_TYPE_TEMPLATE);
        }
        if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.getEditingPreventedWarning(
              automation,
              AutomationType.AUTOMATION_TYPE_DEFAULT,
            );
          }
          return this.translateService.stream('AUTOMATIONS.EDITOR.NO_EDITING_BUILT_IN_AUTOMATIONS');
        }
        return this.automationService.getEditingPreventedWarning(automation, AutomationType.AUTOMATION_TYPE_STANDARD);
      }),
    );
  }

  copyAutomation(a: Automation): Observable<string> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.copyAutomation(a, AutomationType.AUTOMATION_TYPE_TEMPLATE);
        }
        if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.copyAutomation(a, AutomationType.AUTOMATION_TYPE_DEFAULT);
          }
          return throwError('partners cannot copy built in automations');
        }
        return this.automationService.copyAutomation(a, AutomationType.AUTOMATION_TYPE_STANDARD);
      }),
    );
  }

  deleteAutomation(a: Automation): Observable<boolean> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      take(1),
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.deleteAutomation(a, AutomationType.AUTOMATION_TYPE_TEMPLATE);
        }
        if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.deleteAutomation(a, AutomationType.AUTOMATION_TYPE_DEFAULT);
          }
          return throwError('partners cannot delete built in automations');
        }
        return this.automationService.deleteAutomation(a, AutomationType.AUTOMATION_TYPE_STANDARD);
      }),
    );
  }

  getTags(partnerId: string, marketIds: string[]): Observable<string[]> {
    return combineLatest([
      this.isTemplateView$,
      this.isBuiltInAutomationView$,
      this.isManageBuiltInAutomationsView$,
    ]).pipe(
      switchMap(([isTemplateView, isBuiltInAutomationView, isManageBuiltInAutomationsView]) => {
        if (isTemplateView) {
          return this.automationService.getTags(PUBLISHING_PID, [], AutomationType.AUTOMATION_TYPE_TEMPLATE);
        }
        if (isBuiltInAutomationView) {
          if (isManageBuiltInAutomationsView) {
            return this.automationService.getTags(PUBLISHING_PID, [], AutomationType.AUTOMATION_TYPE_DEFAULT);
          }
          return throwError('partners cannot get tags for built in automations');
        }
        return this.automationService.getTags(partnerId, marketIds, AutomationType.AUTOMATION_TYPE_STANDARD);
      }),
    );
  }

  subscribe(partnerId: string, marketIds: string[], automationId: string): Observable<boolean> {
    return this.automationService.subscribe(partnerId, marketIds, automationId).pipe(take(1));
  }

  unsubscribe(automationId: string): Observable<boolean> {
    return this.automationService.unsubscribe(automationId).pipe(take(1));
  }

  isSubscribed(automationId: string): Observable<boolean> {
    return this.automationService.isSubscribed(automationId);
  }

  private getViewMode(): Observable<ViewMode> {
    return combineLatest([this.currentAutomationService.automation$, this.namespace$]).pipe(
      map(([automation, namespace]) => {
        if (automation?.namespace === namespace) {
          return ViewMode.Edit;
        }
        if (!automation?.namespace && namespace === PLATFORM_MANAGEMENT_NAMESPACE) {
          return ViewMode.PlatformManagement;
        }
        return ViewMode.ReadOnly;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getAutomationType(): Observable<AutomationType> {
    return this.currentAutomationService.automation$.pipe(
      map((automation) => {
        if (automation?.isTemplate) {
          return AutomationType.AUTOMATION_TYPE_TEMPLATE;
        }
        if (automation?.isDefaultAutomation) {
          return AutomationType.AUTOMATION_TYPE_DEFAULT;
        }
        return AutomationType.AUTOMATION_TYPE_STANDARD;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  expandGroup(groupId: string): void {
    this.localStorageService.set(groupId, 'expanded');

    this.expandedGroups.update((expandedGroups) => {
      const newExpandedGroups = new Set(expandedGroups);
      newExpandedGroups.add(groupId);
      return newExpandedGroups;
    });
  }

  collapseGroup(groupId: string): void {
    this.localStorageService.remove(groupId);

    this.expandedGroups.update((expandedGroups) => {
      const newExpandedGroups = new Set(expandedGroups);
      newExpandedGroups.delete(groupId);
      return newExpandedGroups;
    });
  }
}
